import { useEffect, useState } from 'react'
import { ensureAnonSession } from '../lib/api/tv'
import { readError } from '../lib/errors'
import { ColorfulTvHomeScreen } from './ColorfulTvHomeScreen'
import { ColorfulTvRoomScreen } from './ColorfulTvRoomScreen'
import { useT } from '../i18n'

/**
 * Colorful TV entrypoint.
 *
 * The TV is a pure display — no sign-in, no host controls, no session
 * attached to any real user. Whoever wants to sponsor a room does it
 * from the main app on their phone (where they're already signed in
 * with email); that flow creates the room and gives them a code to
 * type into /tv/colorful on the big screen.
 *
 * Paths:
 *   /tv/colorful             → landing with a code-entry form
 *   /tv/colorful/ABCDEF      → the read-only room display
 */
export function ColorfulTvApp() {
  const t = useT()
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const code = parseRoomCode(window.location.pathname)

  useEffect(() => {
    let cancelled = false
    // Anonymous session so Realtime subscriptions + table reads work;
    // RLS allows any authenticated user to read colorful_rooms + players.
    // No display name passed — the TV is a pure display, not a seat.
    // If the user happens to share a browser between the main app and
    // the TV tab, passing a name here would overwrite their real
    // profile.display_name (e.g. 'Jodan' → 'TV (Colorful)').
    ensureAnonSession()
      .then(() => { if (!cancelled) setReady(true) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  if (error) return <TvMessage title={t("Couldn't start the TV session")} body={error} />
  if (!ready) return <TvMessage title={t('Starting up…')} body={t('Connecting.')} />

  if (code) return <ColorfulTvRoomScreen code={code} />
  return <ColorfulTvHomeScreen />
}

function parseRoomCode(path: string): string | null {
  const m = path.match(/^\/tv\/colorful\/?([A-Za-z0-9]+)?\/?$/)
  const raw = m?.[1]
  if (!raw) return null
  return raw.toUpperCase()
}

function TvMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-text p-12">
      <div className="text-center max-w-3xl">
        <h1 className="font-display text-5xl font-bold mb-4">{title}</h1>
        <p className="text-xl text-brand-muted">{body}</p>
      </div>
    </div>
  )
}
